import i18n, { type BackendModule, type ReadCallback } from "i18next";
import { initReactI18next } from "react-i18next";
import { isLocale, LOCALE_META, SUPPORTED_LOCALES, type Locale } from "@assessly/contracts/locales";
import { en, type Dict } from "./locales/en.js";
import { ru } from "./locales/ru.js";
import { resolveInitialLocale } from "./lib/locale.js";

// i18n runtime wiring (the i18n rule, D-430).
//
// WHICH LOCALES. `supportedLngs` is the registry's six. `load: "currentOnly"`
// with `nonExplicitSupportedLngs: false` makes i18next resolve a key in the
// active locale and then in `fallbackLng` only: without them a key missing
// from `zh-Hant` would be looked up in `zh` first, and a Traditional Chinese
// reader would be shown Simplified characters. A key missing everywhere
// renders English, never a raw dotted key.
//
// WHEN EACH DICTIONARY LOADS. English is bundled, because it is the fallback
// for every other locale and must be present at the first paint. Every other
// locale is its own module under src/locales/, fetched by the backend below
// the first time it is needed, so a locale nobody uses costs nobody a
// download. Russian is the one exception today: each area's `*.i18n.ts` still
// imports its own `*.ru.ts`, which keeps the Russian text in the entry chunk
// whatever this file does, so it is registered eagerly rather than fetched.
// When those imports move to src/locales/ru.ts, delete it from `EAGER`.
//
// The INITIAL LANGUAGE is resolved by lib/locale.ts (`?lng=` > device >
// browser > en before sign-in; the switcher adds the user's saved language and
// the tenant default once the profile arrives). `i18nReady` resolves when that
// language's dictionary is loaded, and main.tsx renders after it.

export type { Dict };

const EAGER: Partial<Record<Locale, Dict>> = { en, ru };

const LAZY: Readonly<Record<Locale, () => Promise<Dict>>> = {
  en: () => Promise.resolve(en),
  ru: () => Promise.resolve(ru),
  fr: () => import("./locales/fr.js").then((m) => m.fr),
  zh: () => import("./locales/zh.js").then((m) => m.zh),
  "zh-Hant": () => import("./locales/zh-hant.js").then((m) => m.zhHant),
  hi: () => import("./locales/hi.js").then((m) => m.hi),
};

/** Loads a locale module the first time i18next asks for it. */
const lazyLocales: BackendModule = {
  type: "backend",
  init() {
    /* no options */
  },
  read(language: string, _namespace: string, callback: ReadCallback) {
    const load = (LAZY as Record<string, (() => Promise<Dict>) | undefined>)[language];
    if (load === undefined) {
      callback(null, {});
      return;
    }
    load().then(
      (dict) => {
        callback(null, dict);
      },
      (error: unknown) => {
        callback(error instanceof Error ? error : new Error(String(error)), false);
      },
    );
  },
};

// `<html lang>` follows the active language on every page, the sign-in page
// included, which has no switcher: screen readers pick a voice from it, the
// browser picks hyphenation and spell-check from it, and styles/locales.css
// picks the script's font stack from it (`zh` is written `zh-Hans`).
export function syncHtmlLang(lng: string | undefined): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = LOCALE_META[isLocale(lng) ? lng : "en"].htmlLang;
}
i18n.on("languageChanged", syncHtmlLang);

export const i18nReady: Promise<unknown> = i18n
  .use(lazyLocales)
  .use(initReactI18next)
  .init({
    resources: Object.fromEntries(
      Object.entries(EAGER).map(([locale, dict]) => [locale, { translation: dict }]),
    ),
    partialBundledLanguages: true,
    supportedLngs: [...SUPPORTED_LOCALES],
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    lng: resolveInitialLocale(),
    fallbackLng: "en",
    interpolation: { escapeValue: false },
  });

export default i18n;
